import { useEffect, useState } from "react";
import { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  RouterProvider,
  createRoute,
  createRootRouteWithContext,
  createRouter,
  redirect,
  useNavigate,
  useRouter,
  type RouterHistory,
} from "@tanstack/react-router";
import { Toaster } from "sonner";
import { WorkspaceFrame } from "@/components/shell/AppShell";
import { authSessionQueryOptions, useSession } from "@/data/auth";
import {
  chooseRootWorkspaceSlug,
  readLastWorkspaceSlug,
  writeLastWorkspaceSlug,
} from "@/data/root-redirect";
import {
  useCurrentWorkspace,
  workspacesMeQueryOptions,
} from "@/data/workspaces";
import { BrowseScreen } from "@/screens/browse/BrowseScreen";
import { HomeScreen } from "@/screens/home/HomeScreen";
import { InviteScreen } from "@/screens/invite/InviteScreen";
import { PrivacyPolicy } from "@/screens/legal/PrivacyPolicy";
import { TermsOfService } from "@/screens/legal/TermsOfService";
import { LoginScreen } from "@/screens/login/LoginScreen";
import { OnboardingScreen } from "@/screens/onboarding/OnboardingScreen";
import { SessionScreen } from "@/screens/session/SessionScreen";
import {
  SettingsScreen,
  normalizeSettingsTab,
  type SettingsTab,
} from "@/screens/settings/SettingsScreen";
import {
  captureTelemetryEvent,
  identifyTelemetryUser,
  resetTelemetryUser,
} from "@/app/telemetry/posthog";

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  notFoundComponent: () => (
    <div className="p-9 text-muted-foreground">Not found</div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: validateLoginSearch,
  component: LoginScreen,
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: PrivacyPolicy,
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: TermsOfService,
});

const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite",
  validateSearch: validateInviteSearch,
  component: InviteRouteComponent,
});

const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.ensureQueryData(
      authSessionQueryOptions(),
    );
    if (!session.authenticated || !session.user) {
      throw redirect({
        to: "/login",
        search: loginRedirectSearch(location.href),
      });
    }
  },
  component: AuthenticatedLayout,
});

const rootRedirectRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/",
  component: RootRedirectRouteComponent,
});

const onboardingRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/onboarding",
  validateSearch: validateOnboardingSearch,
  component: OnboardingRouteComponent,
});

const workspaceRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/$workspaceSlug",
  // Onboarding gate: a member may not enter a workspace without a real display
  // name. (The voice-sample requirement — from the ambient-capture product —
  // has been removed; previews and the rest of the app don't need it.)
  beforeLoad: async ({ context, location, params }) => {
    // fetchQuery, not ensureQueryData: completing the name step invalidates
    // these queries, and the gate must AWAIT the fresh flag. ensureQueryData
    // returns the stale cached value and refetches in the background, which
    // bounced a just-onboarded member straight back to the wizard.
    const [session, workspaces] = await Promise.all([
      context.queryClient.fetchQuery(authSessionQueryOptions()),
      context.queryClient.fetchQuery(workspacesMeQueryOptions()),
    ]);
    const membership = workspaces.memberships.find(
      (m) => m.workspace.slug === params.workspaceSlug,
    );
    // Unknown or non-member slugs keep their pre-gate behavior.
    if (!membership) return;
    const needsName = !session.user?.hasRealDisplayName;
    if (!needsName) return;
    captureTelemetryEvent("onboarding_gate_redirected", {
      workspace_slug: params.workspaceSlug,
      needs_name: needsName,
    });
    throw redirect({
      to: "/onboarding",
      search: {
        workspace: params.workspaceSlug,
        return_to: location.href,
      },
    });
  },
  component: Outlet,
});

const workspaceHomeRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "/",
  component: WorkspaceHomeRouteComponent,
});

const workspaceCaptureRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "captures/$sessionId",
  component: WorkspaceCaptureRouteComponent,
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "settings",
  validateSearch: validateSettingsSearch,
  component: WorkspaceSettingsRouteComponent,
});

const workspaceBrowseRoute = createRoute({
  getParentRoute: () => workspaceRoute,
  path: "browse/$source",
  component: WorkspaceBrowseRouteComponent,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  privacyRoute,
  termsRoute,
  inviteRoute,
  authenticatedRoute.addChildren([
    rootRedirectRoute,
    onboardingRoute,
    workspaceRoute.addChildren([
      workspaceHomeRoute,
      workspaceCaptureRoute,
      workspaceSettingsRoute,
      workspaceBrowseRoute,
    ]),
  ]),
]);

export function createAppRouter(options: {
  queryClient: QueryClient;
  history?: RouterHistory;
}) {
  return createRouter({
    routeTree,
    context: { queryClient: options.queryClient },
    history: options.history,
  });
}

type AppRouter = ReturnType<typeof createAppRouter>;

declare module "@tanstack/react-router" {
  interface Register {
    router: AppRouter;
  }
}

export function AppRouterProvider({
  queryClient,
}: {
  queryClient: QueryClient;
}) {
  const [router] = useState(() => createAppRouter({ queryClient }));
  return <RouterProvider router={router} />;
}

function validateLoginSearch(search: Record<string, unknown>) {
  return {
    login_hint: optionalString(search.login_hint),
    return_to: optionalString(search.return_to),
    invitation_token: optionalString(search.invitation_token),
  };
}

function validateInviteSearch(search: Record<string, unknown>) {
  return {
    invitation_token: optionalString(search.invitation_token),
  };
}

function validateSettingsSearch(search: Record<string, unknown>) {
  return {
    tab: normalizeSettingsTab(search.tab),
  };
}

function validateOnboardingSearch(search: Record<string, unknown>) {
  return {
    workspace: optionalString(search.workspace),
    return_to: optionalString(search.return_to),
  };
}

function loginRedirectSearch(href: string): {
  login_hint: string | undefined;
  return_to: string | undefined;
  invitation_token: string | undefined;
} {
  return {
    login_hint: undefined,
    return_to: href && href !== "/" ? href : undefined,
    invitation_token: undefined,
  };
}

function AuthenticatedLayout() {
  const session = useSession();
  const navigate = useNavigate();

  useEffect(() => {
    if (session.status === "authenticated" && session.user) {
      identifyTelemetryUser(session.user);
    } else if (session.status === "anonymous") {
      resetTelemetryUser();
    }
  }, [session.status, session.user]);

  useEffect(() => {
    if (session.status !== "anonymous") return;
    void navigate({
      to: "/login",
      search: {
        login_hint: undefined,
        return_to: undefined,
        invitation_token: undefined,
      },
      replace: true,
    });
  }, [navigate, session.status]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const hadDark = root.classList.contains("dark");
    const hadParable = body.classList.contains("parable-shell");
    if (!hadDark) root.classList.add("dark");
    if (!hadParable) body.classList.add("parable-shell");
    return () => {
      if (!hadDark) root.classList.remove("dark");
      if (!hadParable) body.classList.remove("parable-shell");
    };
  }, []);

  return (
    <div className="flex h-screen w-screen flex-col bg-background text-foreground">
      <Toaster theme="dark" position="bottom-right" richColors closeButton />
      <div className="flex min-h-0 flex-1">
        <Outlet />
      </div>
    </div>
  );
}

function RootRedirectRouteComponent() {
  const navigate = useNavigate();
  const { loading, memberships } = useCurrentWorkspace(null);

  useEffect(() => {
    if (loading) return;
    const workspaceSlug = chooseRootWorkspaceSlug(
      memberships,
      readLastWorkspaceSlug(),
    );
    if (workspaceSlug) {
      writeLastWorkspaceSlug(workspaceSlug);
      void navigate({
        to: "/$workspaceSlug",
        params: { workspaceSlug },
        replace: true,
      });
    } else {
      void navigate({
        to: "/onboarding",
        search: { workspace: undefined, return_to: undefined },
        replace: true,
      });
    }
  }, [loading, memberships, navigate]);

  return null;
}

function OnboardingRouteComponent() {
  const navigate = useNavigate();
  const router = useRouter();
  const search = onboardingRoute.useSearch();
  return (
    <OnboardingScreen
      targetWorkspaceSlug={search.workspace ?? null}
      onDone={(workspaceSlug) => {
        writeLastWorkspaceSlug(workspaceSlug);
        // Resume the deep link the gate interrupted, but only within the
        // workspace the wizard just completed for.
        const returnTo = search.return_to;
        if (
          returnTo &&
          (returnTo === `/${workspaceSlug}` ||
            returnTo.startsWith(`/${workspaceSlug}/`) ||
            returnTo.startsWith(`/${workspaceSlug}?`))
        ) {
          router.history.push(returnTo);
          return;
        }
        void navigate({
          to: "/$workspaceSlug",
          params: { workspaceSlug },
        });
      }}
      onCancel={() => {
        void navigate({ to: "/" });
      }}
    />
  );
}

function InviteRouteComponent() {
  const navigate = useNavigate();
  const search = inviteRoute.useSearch();
  return (
    <InviteScreen
      token={search.invitation_token ?? null}
      onAccepted={(workspaceSlug) => {
        writeLastWorkspaceSlug(workspaceSlug);
        // The workspace route's onboarding gate (plan 029) takes it from
        // here: a new invitee lands in the wizard, an already-onboarded
        // member lands in the workspace.
        void navigate({
          to: "/$workspaceSlug",
          params: { workspaceSlug },
          replace: true,
        });
      }}
      onPicker={() => {
        void navigate({ to: "/" });
      }}
      onLogin={(email) => {
        void navigate({
          to: "/login",
          search: {
            login_hint: email ?? undefined,
            return_to: undefined,
            invitation_token: undefined,
          },
        });
      }}
    />
  );
}

function WorkspaceHomeRouteComponent() {
  const { workspaceSlug } = workspaceHomeRoute.useParams();
  return (
    <WorkspaceFrame workspaceSlug={workspaceSlug} activeView="home">
      {({ highlightedSessionId, onOpenSession }) => (
        <HomeScreen
          workspaceSlug={workspaceSlug}
          highlightedSessionId={highlightedSessionId}
          onOpenSession={(_source, id) => onOpenSession(id)}
        />
      )}
    </WorkspaceFrame>
  );
}

function WorkspaceCaptureRouteComponent() {
  const { workspaceSlug, sessionId } = workspaceCaptureRoute.useParams();
  return (
    <WorkspaceFrame
      workspaceSlug={workspaceSlug}
      activeView="session"
      sessionId={sessionId}
    >
      {({
        isCurrentSessionPinned,
        prevSessionId,
        nextSessionId,
        onOpenSession,
        onOpenHome,
        onPinsChanged,
      }) => (
        <SessionScreen
          workspaceSlug={workspaceSlug}
          sessionId={sessionId}
          isPinned={isCurrentSessionPinned}
          onBack={onOpenHome}
          onPinChanged={onPinsChanged}
          onNavigatePrev={
            prevSessionId ? () => onOpenSession(prevSessionId) : undefined
          }
          onNavigateNext={
            nextSessionId ? () => onOpenSession(nextSessionId) : undefined
          }
        />
      )}
    </WorkspaceFrame>
  );
}

function WorkspaceSettingsRouteComponent() {
  const navigate = useNavigate();
  const { workspaceSlug } = workspaceSettingsRoute.useParams();
  const search = workspaceSettingsRoute.useSearch();
  const onTabChange = (tab: SettingsTab) => {
    void navigate({
      to: "/$workspaceSlug/settings",
      params: { workspaceSlug },
      search: { tab },
    });
  };

  return (
    <SettingsScreen
      tab={search.tab}
      onBack={() => {
        void navigate({
          to: "/$workspaceSlug",
          params: { workspaceSlug },
        });
      }}
      onTabChange={onTabChange}
    />
  );
}

function WorkspaceBrowseRouteComponent() {
  const { workspaceSlug, source } = workspaceBrowseRoute.useParams();
  return (
    <WorkspaceFrame workspaceSlug={workspaceSlug} activeView="browse">
      {({ onOpenSettings }) => (
        <BrowseScreen
          workspaceSlug={workspaceSlug}
          source={source}
          onBack={onOpenSettings}
        />
      )}
    </WorkspaceFrame>
  );
}

function optionalString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}
